import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import {
  workShiftsApi,
  workShiftsQueryKeys,
  WEEKDAY_OPTIONS,
  weekdayLabel,
  type WorkShift,
  type WorkShiftFilter,
} from "../../shared/api/workShifts";
import {
  clinicSessionsApi,
  clinicSessionsQueryKeys,
} from "../../shared/api/clinicSessions";
import { roomsApi, roomQueryKeys } from "../../shared/api/rooms";
import { TimeInput } from "../../shared/ui/TimeInput";
import {
  clinicSettingsApi,
  clinicSettingsQueryKeys,
  parseWorkingHours,
} from "../../shared/api/clinic";
import { hoursOnWeekday, shiftOutsideMessage } from "../../shared/workingHours";
import { uncoveredByWeekday } from "./dutyCoverage";
import { appointmentsApi, appointmentQueryKeys } from "../../shared/api/appointments";
import { apiError } from "../../shared/api/client";
import { DepartmentSelect } from "../../shared/ui/DepartmentSelect";
import {
  useRowSelection,
  BulkDeleteBar,
  runBulkDelete,
} from "../../shared/ui/bulkSelect";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ANY = "— Bất kỳ —";

/** Native `<select>` styled to match the shadcn Input control. */
const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

/** Cài đặt Lịch làm việc — khai báo ca làm việc theo Khoa/Phòng/Bác sĩ cho đặt lịch. */
/**
 * #19937 — nhúng trong tab "Lịch trực" của trang Lịch làm việc (bác sĩ); {@code embedded} thì tiêu
 * đề nhỏ lại vì trang cha đã có tiêu đề.
 */
export function WorkShiftsSettingsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<WorkShift | "new" | null>(null);

  const [fDept, setFDept] = useState<string | null>(null);
  const [fRoom, setFRoom] = useState<string | null>(null);
  const [fDoctor, setFDoctor] = useState<string | null>(null);
  const [fWeekday, setFWeekday] = useState<number | null>(null);

  const filter: WorkShiftFilter = {
    departmentId: fDept,
    roomId: fRoom,
    doctorId: fDoctor,
    weekday: fWeekday,
  };

  const rooms = useQuery({
    queryKey: roomQueryKeys.list(false),
    queryFn: () => roomsApi.list(false),
  });
  const doctors = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
  });

  const query = useQuery({
    queryKey: workShiftsQueryKeys.list(filter),
    queryFn: () => workShiftsApi.list(filter),
  });

  const rows = useMemo(() => query.data ?? [], [query.data]);

  function refresh() {
    qc.invalidateQueries({ queryKey: workShiftsQueryKeys.all() });
  }

  const remove = useMutation({
    mutationFn: (id: string) => workShiftsApi.remove(id),
    onSuccess: refresh,
  });

  const sel = useRowSelection();
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete([...sel.selectedIds], workShiftsApi.remove);
    setBulkPending(false);
    sel.clear();
    refresh();
    setBulkError(
      failed.length
        ? `${failed.length} lịch làm việc không xoá được (đang được dùng).`
        : null,
    );
  }

  const allChecked =
    rows.length > 0 && rows.every((s) => sel.selectedIds.has(s.id));
  const someChecked =
    rows.some((s) => sel.selectedIds.has(s.id)) && !allChecked;

  function toggleAll() {
    const next = new Set(sel.selectedIds);
    if (rows.every((s) => next.has(s.id))) rows.forEach((s) => next.delete(s.id));
    else rows.forEach((s) => next.add(s.id));
    sel.setSelectedIds(next);
  }

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-3xl">
          {embedded ? (
            <h2 className="text-base font-semibold text-foreground">Lịch trực</h2>
          ) : (
            <h1 className="text-xl font-bold text-foreground">Lịch làm việc</h1>
          )}
          <p className="mt-1 text-sm text-muted-foreground">
            Khai báo ca làm việc theo Khoa / Phòng / Bác sĩ — lặp theo thứ trong
            tuần hoặc theo ngày cụ thể, tính theo buổi khám hoặc khung giờ. Đây là
            cơ sở để hệ thống tính giờ còn trống và tự điền bác sĩ trực khi đặt lịch.
          </p>
        </div>
        <div className="shrink-0">
          <Button type="button" onClick={() => setEditing("new")}>
            <Plus />
            Tạo lịch làm việc
          </Button>
        </div>
      </div>

      <DutyCoverageWarning />

      <div className="mt-4 flex flex-wrap gap-3">
        <div className="grid min-w-[180px] flex-1 gap-1">
          <Label>Khoa</Label>
          <DepartmentSelect value={fDept} onChange={setFDept} />
        </div>
        <div className="grid min-w-[180px] flex-1 gap-1">
          <Label htmlFor="filter-room">Phòng</Label>
          <select
            id="filter-room"
            className={SELECT_CLASS}
            value={fRoom ?? ""}
            onChange={(e) => setFRoom(e.target.value || null)}
          >
            <option value="">{ANY}</option>
            {(rooms.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.code ? ` (${r.code})` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="grid min-w-[180px] flex-1 gap-1">
          <Label htmlFor="filter-doctor">Bác sĩ</Label>
          <select
            id="filter-doctor"
            className={SELECT_CLASS}
            value={fDoctor ?? ""}
            onChange={(e) => setFDoctor(e.target.value || null)}
          >
            <option value="">{ANY}</option>
            {(doctors.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid min-w-[140px] flex-1 gap-1">
          <Label htmlFor="filter-weekday">Thứ</Label>
          <select
            id="filter-weekday"
            className={SELECT_CLASS}
            value={fWeekday ?? ""}
            onChange={(e) =>
              setFWeekday(e.target.value === "" ? null : Number(e.target.value))
            }
          >
            <option value="">Tất cả</option>
            {WEEKDAY_OPTIONS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {query.isError && (
        <p className="mt-3 text-sm text-destructive">Không tải được lịch làm việc.</p>
      )}
      {query.isLoading && (
        <p className="mt-3 text-sm text-muted-foreground">Đang tải…</p>
      )}

      {bulkError && <p className="mt-3 text-sm text-destructive">{bulkError}</p>}

      {!query.isLoading && rows.length > 0 && (
        <>
          <div className="mt-4">
            <BulkDeleteBar
              count={sel.count}
              noun="lịch làm việc"
              pending={bulkPending}
              onClear={sel.clear}
              onDelete={bulkDelete}
            />
          </div>
          <Card className="mt-4 overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-9 text-center">
                    <Checkbox
                      aria-label="Chọn tất cả"
                      checked={allChecked ? true : someChecked ? "indeterminate" : false}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead>Khoa</TableHead>
                  <TableHead>Phòng</TableHead>
                  <TableHead>Bác sĩ</TableHead>
                  <TableHead>Khi nào</TableHead>
                  <TableHead>Buổi / Khung giờ</TableHead>
                  <TableHead>Trạng thái</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-center">
                      <Checkbox
                        aria-label="Chọn dòng"
                        checked={sel.selectedIds.has(s.id)}
                        onCheckedChange={() => sel.toggle(s.id)}
                      />
                    </TableCell>
                    <TableCell>{s.departmentName ?? <Any />}</TableCell>
                    <TableCell>{s.roomName ?? <Any />}</TableCell>
                    <TableCell>{s.doctorName ?? <Any />}</TableCell>
                    <TableCell>
                      {s.specificDate
                        ? s.specificDate
                        : s.weekday != null
                          ? weekdayLabel(s.weekday)
                          : "—"}
                    </TableCell>
                    <TableCell>
                      {s.sessionCode ? (
                        <span className="font-mono tabular-nums">{s.sessionCode}</span>
                      ) : s.startTime && s.endTime ? (
                        <span className="font-mono tabular-nums">
                          {s.startTime}–{s.endTime}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {s.active ? (
                        <Badge variant="success">Đang kích hoạt</Badge>
                      ) : (
                        <Badge variant="muted">Đã ngưng</Badge>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => setEditing(s)}
                        >
                          Sửa
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                "Xoá lịch làm việc này? Thao tác không thể hoàn tác."
                              )
                            ) {
                              remove.mutate(s.id);
                            }
                          }}
                        >
                          Xoá
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {!query.isLoading && rows.length === 0 && (
        <Card className="mt-4 p-6 text-sm text-muted-foreground">
          Không có lịch làm việc nào khớp bộ lọc. Bấm <em>+ Tạo lịch làm việc</em> để
          thêm mới.
        </Card>
      )}

      {editing && (
        <WorkShiftFormModal
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function Any() {
  return <span className="text-muted-foreground">{ANY}</span>;
}

type WhenMode = "weekday" | "date";
type TimeMode = "session" | "slot";

function WorkShiftFormModal({
  item,
  onClose,
  onSaved,
}: {
  item: WorkShift | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [departmentId, setDepartmentId] = useState<string | null>(
    item?.departmentId ?? null
  );
  const [roomId, setRoomId] = useState<string | null>(item?.roomId ?? null);
  const [doctorProfileId, setDoctorProfileId] = useState<string | null>(
    item?.doctorProfileId ?? null
  );

  const [whenMode, setWhenMode] = useState<WhenMode>(
    item?.specificDate ? "date" : "weekday"
  );
  const [weekdays, setWeekdays] = useState<number[]>(
    item?.weekday != null ? [item.weekday] : []
  );
  const [specificDate, setSpecificDate] = useState<string>(item?.specificDate ?? "");

  const [timeMode, setTimeMode] = useState<TimeMode>(
    item?.sessionCode ? "session" : "slot"
  );
  const [sessionCode, setSessionCode] = useState<string>(item?.sessionCode ?? "");
  const [startTime, setStartTime] = useState<string>(item?.startTime ?? "");
  const [endTime, setEndTime] = useState<string>(item?.endTime ?? "");

  // #19936 — sức chứa không còn khai ở Phân ca (chỉ ở Giới hạn & nhắc lịch) và không được đọc ở
  // đâu. Cột vẫn NOT NULL tới khi bị xoá: giữ giá trị cũ khi sửa, ca mới nhận mặc định của cột.
  const capacity = item?.capacity ?? 20;
  const [error, setError] = useState<string | null>(null);

  const rooms = useQuery({
    queryKey: roomQueryKeys.list(false),
    queryFn: () => roomsApi.list(false),
  });
  const doctors = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
  });
  const sessions = useQuery({
    queryKey: clinicSessionsQueryKeys.all(),
    queryFn: () => clinicSessionsApi.list(),
  });

  const canMultiWeekday = !item; // multi-create only when adding new

  function toggleWeekday(v: number) {
    setWeekdays((prev) => {
      if (prev.includes(v)) return prev.filter((x) => x !== v);
      if (!canMultiWeekday) return [v];
      return [...prev, v].sort((a, b) => a - b);
    });
  }

  const save = useMutation({
    mutationFn: async () => {
      const cap = capacity;
      const timePart =
        timeMode === "session"
          ? { sessionCode: sessionCode || null, startTime: null, endTime: null }
          : {
              sessionCode: null,
              startTime: startTime || null,
              endTime: endTime || null,
            };

      if (item) {
        const whenPart =
          whenMode === "date"
            ? { weekday: null, specificDate: specificDate || null }
            : { weekday: weekdays[0] ?? null, specificDate: null };
        return workShiftsApi.update(item.id, {
          departmentId,
          roomId,
          doctorProfileId,
          capacity: cap,
          active: item.active,
          ...whenPart,
          ...timePart,
        });
      }

      // Create: a specific date is one row; weekday can create several.
      if (whenMode === "date") {
        return workShiftsApi.create({
          departmentId,
          roomId,
          doctorProfileId,
          weekday: null,
          specificDate: specificDate || null,
          capacity: cap,
          ...timePart,
        });
      }
      const days = weekdays.length > 0 ? weekdays : [null];
      let last: WorkShift | undefined;
      for (const wd of days) {
        last = await workShiftsApi.create({
          departmentId,
          roomId,
          doctorProfileId,
          weekday: wd,
          specificDate: null,
          capacity: cap,
          ...timePart,
        });
      }
      return last;
    },
    onSuccess: onSaved,
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const whenValid =
    whenMode === "date" ? !!specificDate : true; // weekday empty = "any day" wildcard is allowed
  const timeValid =
    timeMode === "session"
      ? !!sessionCode
      : !!startTime && !!endTime;

  // #19938 — báo NGAY khi nhập: giờ ngược, hoặc ca vượt giờ làm việc của (các) thứ đã chọn. Server
  // vẫn kiểm lại khi lưu. Ca theo buổi không kiểm ở đây (buổi đã được kiểm khi lưu Buổi khám).
  const clinic = useQuery({
    queryKey: clinicSettingsQueryKeys.current(),
    queryFn: () => clinicSettingsApi.get(),
    staleTime: 60_000,
  });
  const checkDays =
    whenMode === "date"
      ? specificDate
        ? [((new Date(specificDate + "T00:00:00").getDay() + 6) % 7) + 1]
        : []
      : weekdays.length > 0
        ? weekdays
        : [1, 2, 3, 4, 5, 6, 7];
  const liveError =
    timeMode !== "session" && startTime && endTime
      ? startTime >= endTime
        ? "Giờ bắt đầu phải trước giờ kết thúc."
        : shiftOutsideMessage(
            parseWorkingHours(clinic.data?.workingHoursJson ?? null),
            checkDays,
            startTime,
            endTime,
          )
      : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {item ? "Sửa lịch làm việc" : "Tạo lịch làm việc mới"}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 grid gap-3">
            <div className="grid gap-1">
              <Label>Khoa</Label>
              <DepartmentSelect value={departmentId} onChange={setDepartmentId} />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="form-room">Phòng</Label>
              <select
                id="form-room"
                className={SELECT_CLASS}
                value={roomId ?? ""}
                onChange={(e) => setRoomId(e.target.value || null)}
              >
                <option value="">{ANY}</option>
                {(rooms.data ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.code ? ` (${r.code})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-1">
              <Label htmlFor="form-doctor">Bác sĩ</Label>
              <select
                id="form-doctor"
                className={SELECT_CLASS}
                value={doctorProfileId ?? ""}
                onChange={(e) => setDoctorProfileId(e.target.value || null)}
              >
                <option value="">{ANY}</option>
                {(doctors.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <fieldset className="grid gap-2 rounded-md border border-input p-3">
              <legend className="px-1.5 text-sm font-semibold">Khi nào</legend>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="whenMode"
                    checked={whenMode === "weekday"}
                    onChange={() => setWhenMode("weekday")}
                  />
                  Lặp theo thứ
                </label>
                <label className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="whenMode"
                    checked={whenMode === "date"}
                    onChange={() => setWhenMode("date")}
                  />
                  Ngày cụ thể
                </label>
              </div>
              {whenMode === "weekday" ? (
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAY_OPTIONS.map((w) => {
                    const on = weekdays.includes(w.value);
                    return (
                      <Button
                        type="button"
                        key={w.value}
                        variant={on ? "default" : "secondary"}
                        size="sm"
                        className="min-w-12"
                        onClick={() => toggleWeekday(w.value)}
                      >
                        {w.short}
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <Input
                  type="date"
                  value={specificDate}
                  onChange={(e) => setSpecificDate(e.target.value)}
                />
              )}
              {whenMode === "weekday" && !item && (
                <small className="text-muted-foreground">
                  Chọn nhiều thứ để tạo nhiều ca cùng lúc. Bỏ trống = áp dụng mọi ngày.
                </small>
              )}
            </fieldset>

            <fieldset className="grid gap-2 rounded-md border border-input p-3">
              <legend className="px-1.5 text-sm font-semibold">Thời gian</legend>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="timeMode"
                    checked={timeMode === "session"}
                    onChange={() => setTimeMode("session")}
                  />
                  Theo buổi
                </label>
                <label className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="timeMode"
                    checked={timeMode === "slot"}
                    onChange={() => setTimeMode("slot")}
                  />
                  Theo khung giờ
                </label>
              </div>
              {timeMode === "session" ? (
                <select
                  className={SELECT_CLASS}
                  aria-label="Buổi khám"
                  value={sessionCode}
                  onChange={(e) => setSessionCode(e.target.value)}
                >
                  <option value="">— Chọn buổi —</option>
                  {(sessions.data ?? [])
                    .filter((s) => s.active || s.code === sessionCode)
                    .map((s) => (
                      <option key={s.id} value={s.code}>
                        {s.label} ({s.startTime}–{s.endTime})
                      </option>
                    ))}
                </select>
              ) : (
                <div className="flex flex-wrap gap-3">
                  <div className="grid flex-1 gap-1">
                    <Label htmlFor="form-start">Giờ bắt đầu</Label>
                    <TimeInput id="form-start" value={startTime} onChange={setStartTime} />
                  </div>
                  <div className="grid flex-1 gap-1">
                    <Label htmlFor="form-end">Giờ kết thúc</Label>
                    <TimeInput id="form-end" value={endTime} onChange={setEndTime} />
                  </div>
                  {liveError && (
                    <p
                      role="alert"
                      data-testid="shift-live-error"
                      className="m-0 w-full text-sm text-destructive"
                    >
                      {liveError}
                    </p>
                  )}
                </div>
              )}
            </fieldset>

          </div>

          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          <DialogFooter className="mt-4">
            <Button type="button" variant="secondary" onClick={onClose}>
              Đóng
            </Button>
            <Button
              type="submit"
              disabled={
                save.isPending ||
                !whenValid ||
                !timeValid ||
                !!liveError
              }
            >
              {save.isPending
                ? "Đang lưu…"
                : item
                  ? "Lưu thay đổi"
                  : "Tạo lịch làm việc"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * #19954 — khoảng giờ của Buổi khám chưa có bác sĩ nào trực, theo thứ. Lưới đặt lịch lấy từ Buổi
 * khám nên những khoảng này VẪN nhận lịch, chỉ là lịch không có bác sĩ (lễ tân chỉ định sau) —
 * cảnh báo, không chặn.
 */
function DutyCoverageWarning() {
  // Đúng bộ buổi engine đặt lịch đọc (cấp chi nhánh hoặc mức phòng khám), không phải danh sách
  // mức phòng khám của màn Buổi khám.
  const sessions = useQuery({
    queryKey: clinicSessionsQueryKeys.effective(),
    queryFn: () => clinicSessionsApi.effective(),
  });
  const shifts = useQuery({
    queryKey: workShiftsQueryKeys.list({}),
    queryFn: () => workShiftsApi.list({}),
  });
  const clinic = useQuery({
    queryKey: clinicSettingsQueryKeys.current(),
    queryFn: () => clinicSettingsApi.get(),
    staleTime: 60_000,
  });
  const groups = useMemo(() => {
    if (!sessions.data || !shifts.data) return [];
    const wh = parseWorkingHours(clinic.data?.workingHoursJson ?? null);
    const open = WEEKDAY_OPTIONS.map((w) => w.value).filter((d) => hoursOnWeekday(wh, d));
    // Chưa khai giờ làm việc: xét các thứ đã có ca lặp của bác sĩ — không bịa ra Chủ nhật.
    const days =
      open.length > 0
        ? open
        : [
            ...new Set(
              shifts.data
                .filter((w) => w.active && w.doctorProfileId && w.weekday && !w.specificDate)
                .map((w) => w.weekday as number)
            ),
          ].sort();
    return uncoveredByWeekday(sessions.data.sessions, shifts.data, days);
  }, [sessions.data, shifts.data, clinic.data]);

  if (groups.length === 0) return null;
  return (
    <div
      data-testid="duty-coverage-warning"
      role="status"
      className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
    >
      <p className="font-medium">Có khung giờ của Buổi khám chưa có bác sĩ trực.</p>
      <p className="mt-1">
        Bệnh nhân vẫn đặt được các khung này, nhưng lịch sẽ không có bác sĩ — lễ tân phải chỉ định
        sau. Nên xếp ca trực cho bác sĩ vào các khoảng sau:
      </p>
      <ul className="mt-1.5 list-disc pl-5">
        {groups.map((g) => (
          <li key={g.weekdays.join(",")}>
            <span className="font-medium">
              {g.weekdays.map((d) => weekdayLabel(d)).join(", ")}
            </span>
            {": "}
            <span className="font-mono">
              {g.ranges.map((r) => `${r.label} ${r.start}–${r.end}`).join(" · ")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
